import React, { useCallback, useEffect, useState } from "react";

// ──────────────────────────────────────────
// AdminAutomation — 자동화 상태 (관리자)
//   GET  /api/onto/admin/automation-status   (admin_token Bearer)
//   POST /api/onto/admin/amendment-gosi/run  변경고시 감시 지금 실행
//   POST /api/onto/admin/precedent-crawl/run 데일리 사례 크롤 지금 실행
//   POST /api/onto/admin/customs-requirement-monitor/run 제226조·0205 지금 확인
//   관세청 자동 수집 2종의 최근 실행 기록(automation_job_status)과
//   변경고시 반영 현황(현행 고시 vs DB 최신·superseded·검수 큐)을 한 화면에.
// ──────────────────────────────────────────

const JOB_META = {
  "amendment-gosi": {
    title: "품목분류 변경고시 감시",
    desc: "law.go.kr 현행 발령번호를 매일 07:30에 확인해, 새 고시가 있으면 별표를 내려받아 파싱·병합 후 변경 전 결정을 superseded로 반영합니다.",
    runPath: "/api/onto/admin/amendment-gosi/run",
    runKey: "amendment_gosi",
  },
  "precedent-crawl": {
    title: "품목분류사례 데일리 크롤",
    desc: "관세청 CLIP 국내 분류사례를 매일 04:00에 증분 수집(목록→적재→상세→임베딩)합니다.",
    runPath: "/api/onto/admin/precedent-crawl/run",
    runKey: "precedent_crawl",
  },
  "customs-requirement-monitor": {
    title: "세관장확인사항 변경 감시",
    desc: "제226조 고시와 관세청 공지를 30분마다 확인합니다. 변경 HSK가 작고 명확하면 UNI-PASS 0205의 해당 코드와 승인기관만 부분 동기화하고, 대량·모호 변경은 자동 중단 후 메일과 수동 가이드를 남깁니다.",
    runPath: "/api/onto/admin/customs-requirement-monitor/run",
    runKey: "customs_requirement",
  },
};

const STATUS_LABEL = {
  up_to_date: ["최신", "#137333", "#e6f4ea"],
  applied: ["신규 반영됨", "#1a73e8", "#eff6ff"],
  rehearsal_ok: ["리허설 통과", "#137333", "#e6f4ea"],
  ok: ["정상", "#137333", "#e6f4ea"],
  baseline: ["기준선 등록", "#5f6368", "#f1f3f4"],
  no_change: ["변경 없음", "#137333", "#e6f4ea"],
  waiting_source: ["원천 반영 대기", "#b06000", "#fef7e0"],
  manual_required: ["수동 조치 필요", "#a50e0e", "#fce8e6"],
  running: ["실행 중", "#b06000", "#fef7e0"],
  error: ["오류", "#a50e0e", "#fce8e6"],
};

const AdminAutomation = ({ API_BASE_URL }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);

  const authHeaders = () => {
    const t = localStorage.getItem("admin_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const reload = useCallback(() => {
    fetch(`${API_BASE_URL}/api/onto/admin/automation-status`, { headers: authHeaders() })
      .then((r) => {
        if (r.status === 401 || r.status === 403) throw new Error("관리자 인증 만료");
        if (!r.ok) throw new Error(`조회 실패(${r.status})`);
        return r.json();
      })
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
  }, [reload]);

  const runJob = async (path, label) => {
    setNotice("");
    try {
      const r = await fetch(`${API_BASE_URL}${path}`, { method: "POST", headers: authHeaders() });
      if (r.status === 401 || r.status === 403) throw new Error("관리자 인증 만료");
      const d = await r.json();
      setNotice(d.started ? `${label} 실행을 시작했습니다 — 잠시 후 상태가 갱신됩니다.` : `${label}이(가) 이미 실행 중입니다.`);
      setTimeout(reload, 3000);
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return <div style={{ padding: 24, fontSize: 13 }}>불러오는 중…</div>;

  const amend = (data && data.amendment) || {};
  const customs = (data && data.customs_requirement) || {};
  const jobs = (data && data.jobs) || [];
  const runningMap = (data && data.running) || {};
  const jobBy = Object.fromEntries(jobs.map((j) => [j.job_name || j.jobName, j]));
  const gosiSynced = amend.db_latest_gosi && jobBy["amendment-gosi"] &&
    (() => {
      try {
        const det = JSON.parse(jobBy["amendment-gosi"].detail || "{}");
        return det.current && det.current.no === amend.db_latest_gosi;
      } catch (e) { return null; }
    })();

  return (
    <div style={{ padding: 24, maxWidth: 760 }}>
      <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>자동화 상태</h3>
      <div style={{ fontSize: 12, color: "#666", marginBottom: 16, lineHeight: 1.6 }}>
        관세청 데이터 자동 수집 잡의 실행 기록입니다. 크론이 서버에서 자동으로 돌지만,
        여기서 즉시 실행하거나 마지막 결과를 확인할 수 있습니다.
      </div>
      {error && (
        <div style={{ padding: "8px 12px", background: "#fce8e6", color: "#a50e0e",
          borderRadius: 4, fontSize: 12, marginBottom: 12 }}>
          {error}{String(error).includes("인증") && " — 관리자 로그인(admin_token)이 필요합니다."}
        </div>
      )}
      {notice && (
        <div style={{ padding: "8px 12px", background: "#e6f4ea", color: "#137333",
          borderRadius: 4, fontSize: 12, marginBottom: 12 }}>{notice}</div>
      )}

      {/* 변경고시 반영 현황 요약 */}
      <div style={{ border: "1px solid #e0e0e0", borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>변경고시 반영 현황</div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12 }}>
          <div>
            <div style={{ color: "#888" }}>DB 최신 고시</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              {amend.db_latest_gosi ? `제${amend.db_latest_gosi}호` : "—"}
              {gosiSynced === true && (
                <span style={{ fontSize: 11, color: "#137333", marginLeft: 6 }}>✓ 현행과 일치</span>
              )}
              {gosiSynced === false && (
                <span style={{ fontSize: 11, color: "#a50e0e", marginLeft: 6 }}>현행과 불일치</span>
              )}
            </div>
            <div style={{ color: "#888" }}>{amend.db_latest_date || ""}</div>
          </div>
          <div>
            <div style={{ color: "#888" }}>변경고시 원장</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{amend.sections ?? "—"}건</div>
          </div>
          <div>
            <div style={{ color: "#888" }}>변경된 결정(superseded)</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{amend.superseded ?? "—"}건</div>
          </div>
          <div title="원문 자동 추출이 불완전해 자동 반영을 보류한 고시 수. 원문은 원장에 보존되며, 검수로 쌍을 확정하면(변경고시_검수보정.json) 반영됩니다. 재변경·폐지 등 처분이 끝난 고시는 제외.">
            <div style={{ color: "#888" }}>검수 대기 ⓘ</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{amend.review_queue ?? "—"}건</div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#999", marginTop: 8, lineHeight: 1.5 }}>
          변경된 결정은 삭제하지 않고 상태로 보존됩니다(사례 화면에 "변경됨" 표시). 검수 대기는
          자동 추출이 불완전한 고시로, 검수 확정 시 자동 반영됩니다 — 판정·검색에는 이미 반영분만 쓰입니다.
        </div>
      </div>

      {/* 관세법 제226조·UNI-PASS 0205 동기화 현황 */}
      <div style={{ border: "1px solid #d2e3fc", borderRadius: 8, padding: "14px 16px", marginBottom: 16,
        background: "#fbfdff" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>세관장확인사항 0205 운영 스냅샷</div>
          <button
            onClick={() => runJob("/api/onto/admin/customs-requirement-monitor/audit", "0205 월간 경량 전수 감사")}
            disabled={!!runningMap.customs_requirement}
            style={{ marginLeft: "auto", padding: "4px 10px", border: "1px solid #aecbfa", borderRadius: 6,
              background: "#fff", color: "#1769e0", fontSize: 11, fontWeight: 600,
              cursor: runningMap.customs_requirement ? "default" : "pointer" }}>
            목록 전수 지문 비교
          </button>
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12, marginBottom: 10 }}>
          <div><div style={{ color: "#888" }}>Active 스냅샷</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>#{customs.active_snapshot_id ?? "—"}</div></div>
          <div><div style={{ color: "#888" }}>요건 행</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{customs.active_rows?.toLocaleString?.() ?? "—"}건</div></div>
          <div><div style={{ color: "#888" }}>HSK</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{customs.active_hsk?.toLocaleString?.() ?? "—"}개</div></div>
          <div><div style={{ color: "#888" }}>승인기관 행</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{customs.active_agencies?.toLocaleString?.() ?? "—"}건</div></div>
        </div>
        <div style={{ fontSize: 11, color: "#667085", lineHeight: 1.6, marginBottom: 10 }}>
          기본 정책: 공지·고시 감지 → 변경 HSK 계산 → 소량만 부분 동기화 → 새 스냅샷 원자 전환.
          200개 또는 현재 HSK의 2%를 넘거나 변경 코드가 불명확하면 전수 수집을 자동 실행하지 않습니다.
        </div>
        {(customs.events || []).length > 0 && (
          <div style={{ borderTop: "1px solid #e8eef7", paddingTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 5 }}>최근 감지·동기화 이력</div>
            {(customs.events || []).slice(0, 6).map((event) => {
              const [label, color, bg] = STATUS_LABEL[event.status] || [event.status, "#666", "#f1f3f4"];
              return (
                <div key={event.id} style={{ padding: "7px 0", borderTop: "1px solid #f0f2f5", fontSize: 11,
                  display: "grid", gridTemplateColumns: "105px 1fr auto", gap: 8, alignItems: "start" }}>
                  <div style={{ color: "#888" }}>{event.created_at || event.issue_date || "—"}</div>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {event.source_url ? <a href={event.source_url} target="_blank" rel="noreferrer"
                        style={{ color: "#1769e0", textDecoration: "none" }}>{event.title} ↗</a> : event.title}
                    </div>
                    <div style={{ color: "#777", marginTop: 2, lineHeight: 1.45 }}>{event.reason || ""}</div>
                    {event.status === "manual_required" && event.detail?.guide && (
                      <div style={{ marginTop: 4, padding: "5px 7px", background: "#fff4f2", color: "#912018",
                        borderRadius: 4, lineHeight: 1.5 }}>{event.detail.guide}</div>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ color, background: bg, padding: "2px 7px", borderRadius: 9, fontWeight: 600 }}>{label}</span>
                    <div style={{ color: "#888", marginTop: 3 }}>{event.affected_hsk_count || 0}개 HSK</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 잡 목록 */}
      {Object.entries(JOB_META).map(([name, meta]) => {
        const row = jobBy[name];
        const isRunning = runningMap[meta.runKey];
        const status = isRunning ? "running" : (row && row.last_status) || null;
        const [label, color, bg] = STATUS_LABEL[status] || ["기록 없음", "#888", "#f1f3f4"];
        let steps = [];
        try { steps = (JSON.parse((row && row.detail) || "{}").steps || []); } catch (e) { /* noop */ }
        return (
          <div key={name} style={{ border: "1px solid #e0e0e0", borderRadius: 8,
            padding: "14px 16px", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>{meta.title}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color, background: bg,
                padding: "2px 8px", borderRadius: 10 }}>{label}</span>
              <button
                onClick={() => runJob(meta.runPath, meta.title)}
                disabled={isRunning}
                style={{ marginLeft: "auto", padding: "4px 12px", border: "1px solid #dadce0",
                  borderRadius: 6, background: isRunning ? "#f1f3f4" : "#fff",
                  color: isRunning ? "#999" : "#1a73e8", fontSize: 12, fontWeight: 600,
                  cursor: isRunning ? "default" : "pointer" }}>
                {isRunning ? "실행 중…" : "지금 실행"}
              </button>
            </div>
            <div style={{ fontSize: 12, color: "#666", margin: "6px 0 8px", lineHeight: 1.6 }}>
              {meta.desc}
            </div>
            <div style={{ fontSize: 11, color: "#888" }}>
              마지막 확인 {row && row.last_check_at ? row.last_check_at : "—"}
              {row && row.last_run_at ? ` · 마지막 실행 ${row.last_run_at}` : ""}
            </div>
            {steps.length > 0 && (
              <div style={{ marginTop: 6, padding: "6px 10px", background: "#f8f9fa",
                borderRadius: 4, fontSize: 11, color: "#555", lineHeight: 1.7 }}>
                {steps.map((s, i) => <div key={i}>{s}</div>)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdminAutomation;
